import { Injectable, signal, computed, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Route, Router } from "@angular/router";
import { routes } from "./../../app.routes";
import { catchError, of, tap, shareReplay, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { UserStateService } from "@core/services/user-state.service";

export interface MenuUsuario {
  lista: {
    path: string;
    nivel: "ADM" | "CR" | "CO";
    codmod: string;
    orden: number;
  }[];
}

@Injectable({
  providedIn: "root",
})
export class RutaDinamicaService {
  private userState = inject(UserStateService);
  routes = routes;

  private menuUsuarioSignal = signal<MenuUsuario | null>(null);
  private menuObservable$: Observable<MenuUsuario | null> | null = null;
  private isLoading = signal(false);
  private currentUser = signal<string | null>(null);

  public http = inject(HttpClient);
  public router = inject(Router);

  public fetchMenuUsuario(): Observable<MenuUsuario | null> {
    const usuario = sessionStorage.getItem("usuario");

    if (usuario && this.currentUser() !== usuario) {
      this.clearMenuCache();
      this.currentUser.set(usuario);
    }

    if (this.menuUsuarioSignal()) {
      return of(this.menuUsuarioSignal());
    }

    if (this.menuObservable$) {
      return this.menuObservable$;
    }

    if (!usuario) {
      this.menuUsuarioSignal.set(null);
      return of(null);
    }
    this.isLoading.set(true);
    const url = `${environment.apiUrl}${this.userState.organization()}/entidades/listar/${usuario}?objeto=arma_menu_usuario/${usuario}`;

    this.menuObservable$ = this.http.get<MenuUsuario>(url).pipe(
      tap((data) => {
        this.menuUsuarioSignal.set(data);
        this.isLoading.set(false);
      }),
      catchError((error) => {
        console.error("Error al cargar menú:", error);
        this.menuUsuarioSignal.set(null);
        this.isLoading.set(false);
        return of(null);
      }),
      shareReplay(1),
      tap(() => {
        this.menuObservable$ = null;
      })
    );

    return this.menuObservable$;
  }

  public getMenuUsuario(): MenuUsuario | null {
    return this.menuUsuarioSignal();
  }

  public isMenuLoading(): boolean {
    return this.isLoading();
  }

  public clearMenuCache(): void {
    this.menuUsuarioSignal.set(null);
    this.menuObservable$ = null;
    this.isLoading.set(false);
    this.currentUser.set(null);
  }

  public getNivelActual() {
    return computed(() => {
      const urlActual = this.router.url;
      const pathSinPrefijo = urlActual.replace(/^\/modulos\//, "");
      const currentRoute = pathSinPrefijo.split("/")[0];

      const routes = this.menuUsuarioSignal();
      if (!routes || !routes.lista) {
        return null;
      }

      const route = routes.lista.find((route) => {
        const cleanedPath = route.path?.replace(/^\/+|\/+$/g, "");
        return cleanedPath === currentRoute;
      });
      return route ? route.nivel : null;
    })();
  }

  public canAcceptDeleteAdd(): boolean {
    return computed(() => {
      const nivel = this.getNivelActual();
      return nivel === "ADM";
    })();
  }

  public canDownload(): boolean {
    return computed(() => {
      const nivel = this.getNivelActual();
      return nivel === "ADM" || nivel === "CR";
    })();
  }

  public hasAccessToPath(path: string): boolean {
    const menu = this.menuUsuarioSignal();
    if (!menu?.lista) return false;

    return menu.lista.some((item) => item.path === path);
  }

  public getAvailablePaths(): string[] {
    const menu = this.menuUsuarioSignal();
    return menu?.lista?.map((item) => item.path) || [];
  }

  public getNivelForPath(path: string): "ADM" | "CR" | "CO" | null {
    const menu = this.menuUsuarioSignal();
    if (!menu?.lista) return null;

    const route = menu.lista.find((item) => item.path === path);
    return route ? route.nivel : null;
  }
}
